import React, { useEffect, useState } from 'react';
import { Col, notification } from 'antd';
import {
  Body,
  Button,
  Colors,
  Drawer,
  Input,
  // OptionType,
  // Select,
  Textarea,
  Tooltip,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import {
  GQLOperations,
  Milestone,
  MilestoneType,
} from 'types/dt-graphql-apollo-v2';
import { v1 as uuidv1 } from 'uuid';
import { gql, useApolloClient } from '@apollo/client';
import {
  createMilestoneMutation,
  updateMilestoneMutation,
} from 'utils/data-validation-graphql';
import {
  // convertCronExpressionToTimeAndDays,
  // convertTimeAndDaysToCronExpression,
  formatMilestoneDateToString,
  getNextMilestoneOrderIndex,
  // getDaysOptions,
  getPreviousMilestoneEndActiveTime,
  term,
  convertISODateToMilestoneDate,
  convertMilestoneDateToISODate,
} from 'utils/project-ruleset';
import { DrawerSteps, Step /* , WIPLabel */ } from 'components';
import { DateRangeSelector } from './components/DateRangeSelector';
// import { DateTimeSelector } from './components/DateTimeSelector';

type NewMilestoneDrawerProps = {
  milestones?: Milestone[] | null;
  milestoneType: MilestoneType | null;
  onCancel: VoidFunction;
  onSuccess: (id: string) => void;
  resetDrawer: boolean;
  schemaId: string;
  selectedMilestone?: Milestone;
  visible: boolean;
};

export const NewMilestoneDrawer = ({
  milestones,
  milestoneType,
  onCancel,
  onSuccess,
  resetDrawer,
  schemaId,
  selectedMilestone,
  visible,
}: NewMilestoneDrawerProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [startActiveDate, setStartActiveDate] = useState<string | null>(null);
  const [endActiveDate, setEndActiveDate] = useState<string | null>(null);
  // const [startScheduleDate, setStartScheduleDate] = useState<string | null>(
  //   null
  // );
  // const [endScheduleDate, setEndScheduleDate] = useState<string | null>(null);
  // const [scheduleDay, setScheduleDay] = useState<OptionType<number>[]>([]);
  // const [scheduleTime, setScheduleTime] = useState<string | null>(null);
  // const [runReportDuringActiveTime, setRunReportDuringActiveTime] = useState(
  //   true
  // );
  const apolloClient = useApolloClient();

  // const scheduleDayValues = scheduleDay.map((option) => Number(option.value));
  // const scheduleDayLabels = scheduleDay.map((option) => option.label);

  const previousEndActiveTime =
    milestoneType === MilestoneType.Default &&
    milestones &&
    getPreviousMilestoneEndActiveTime(selectedMilestone, milestones);
  const previousEndActiveTimeToString =
    previousEndActiveTime && formatMilestoneDateToString(previousEndActiveTime);

  const disableStartActiveTime =
    milestoneType === MilestoneType.Default && !!previousEndActiveTimeToString;

  const activeTimeAllowEmpty: [boolean, boolean] =
    milestoneType === MilestoneType.Default
      ? [disableStartActiveTime, false]
      : [true, true];

  useEffect(() => {
    if (selectedMilestone) {
      setCurrentStep(0);
      setNameInput(selectedMilestone.name || '');
      setDescriptionInput(selectedMilestone.description || '');
      setStartActiveDate(
        selectedMilestone?.startActiveTime
          ? convertISODateToMilestoneDate(selectedMilestone.startActiveTime)
          : null
      );
      setEndActiveDate(
        selectedMilestone?.endActiveTime
          ? convertISODateToMilestoneDate(selectedMilestone.endActiveTime)
          : null
      );
      // setStartScheduleDate(
      //   selectedMilestone?.startScheduleTime
      //     ? convertISODateToMilestoneDate(selectedMilestone.startScheduleTime)
      //     : null
      // );
      // setEndScheduleDate(
      //   selectedMilestone?.endScheduleTime
      //     ? convertISODateToMilestoneDate(selectedMilestone.endScheduleTime)
      //     : null
      // );
      // const convertSchedule = convertCronExpressionToTimeAndDays(
      //   selectedMilestone.schedule
      // );
      // setScheduleDay(
      //   (convertSchedule?.days && getDaysOptions(convertSchedule?.days)) || []
      // );
      // setScheduleTime(convertSchedule?.time || null);
      // setRunReportDuringActiveTime(!selectedMilestone?.startScheduleTime);
    } else {
      onResetDrawer();
    }
  }, [selectedMilestone]);

  const checkInvalidMilestoneActiveTimePeriod = () => {
    if (milestoneType !== MilestoneType.Default) return false;
    if (!startActiveDate === !previousEndActiveTimeToString) return true;
    return !endActiveDate;
  };

  const checkInvalidStaticMilestoneActiveTimePeriod = () => {
    if (milestoneType !== MilestoneType.Static) return false;
    return !!startActiveDate !== !!endActiveDate;
  };

  const hasInvalidName = !nameInput.trim();
  const hasInvalidMilestoneActiveTimePeriod =
    checkInvalidMilestoneActiveTimePeriod();
  const hasInvalidStaticMilestoneActiveTimePeriod =
    checkInvalidStaticMilestoneActiveTimePeriod();
  const hasInvalidActiveTimePeriod =
    hasInvalidMilestoneActiveTimePeriod ||
    hasInvalidStaticMilestoneActiveTimePeriod;
  // const hasInvalidRunReportSchedule =
  //   !runReportDuringActiveTime && (!startScheduleDate || !endScheduleDate);
  const hasInvalidValues = hasInvalidName || hasInvalidActiveTimePeriod; // || hasInvalidRunReportSchedule;

  const handleErrorMessages = () => {
    if (hasInvalidName) {
      return 'The provided name is missing or invalid';
    }
    if (hasInvalidActiveTimePeriod) {
      return 'The provided active time period is missing or invalid';
    }
    // if (hasInvalidRunReportSchedule) {
    //   return 'The provided report schedule is missing or invalid';
    // }
    return 'Unable to save the collection due to invalid configurations';
  };

  const onResetDrawer = () => {
    setNameInput('');
    setDescriptionInput('');
    setStartActiveDate('');
    setEndActiveDate('');
    // setStartScheduleDate('');
    // setEndScheduleDate('');
    // setScheduleTime(null);
    // setScheduleDay([]);
    // setRunReportDuringActiveTime(true);
    setCurrentStep(0);
  };

  const onCreateMilestone = async () => {
    if (creating || hasInvalidValues) return;
    setCreating(true);
    try {
      // const time = scheduleTime?.length ? scheduleTime : null;
      // const days = scheduleDayValues?.length ? scheduleDayValues : null;
      // const hasValidSchedule = !!time && !!days;
      const orderIndex = getNextMilestoneOrderIndex(milestones);
      const milestone = {
        externalId: uuidv1(),
        space: '',
        projectId: schemaId,
        name: nameInput.trim(),
        description: descriptionInput.trim(),
        milestoneType,
        orderIndex,
        startActiveTime: startActiveDate
          ? convertMilestoneDateToISODate(startActiveDate)
          : undefined,
        endActiveTime: endActiveDate
          ? convertMilestoneDateToISODate(endActiveDate)
          : undefined,
        // schedule: hasValidSchedule
        //   ? convertTimeAndDaysToCronExpression({
        //       time,
        //       days,
        //     })
        //   : null,
        // startScheduleTime:
        //   !runReportDuringActiveTime && startScheduleDate
        //     ? convertMilestoneDateToISODate(startScheduleDate)
        //     : null,
        // endScheduleTime:
        //   !runReportDuringActiveTime && endScheduleDate
        //     ? convertMilestoneDateToISODate(endScheduleDate)
        //     : null,
      };
      const query = createMilestoneMutation(milestone);
      const response = await apolloClient.mutate({
        mutation: gql(query),
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      const milestoneId = response.data?.Create_Milestone?.externalId;

      await onSuccess(milestoneId);
      notification.success({
        message: `${term.Milestone} created`,
        description: `Successfully created the ${
          term.milestone
        } "${nameInput.trim()}"`,
      });
      onResetDrawer();
      onCancel();
    } catch (err: any) {
      notification.error({
        message: `Could not add ${term.milestone}`,
        description: err.toString(),
      });
    } finally {
      setCreating(false);
    }
  };

  const onUpdateMilestone = async () => {
    if (creating || hasInvalidValues || !selectedMilestone) return;
    setCreating(true);
    try {
      // const time = scheduleTime?.length ? scheduleTime : null;
      // const days = scheduleDayValues?.length ? scheduleDayValues : null;
      // const hasValidSchedule = !!time && !!days;
      const updatedMilestone = {
        externalId: selectedMilestone.externalId,
        space: '',
        projectId: schemaId,
        name: nameInput.trim(),
        description: descriptionInput.trim(),
        orderIndex: selectedMilestone?.orderIndex || null,
        milestoneType,
        startActiveTime: startActiveDate
          ? convertMilestoneDateToISODate(startActiveDate)
          : undefined,
        endActiveTime: endActiveDate
          ? convertMilestoneDateToISODate(endActiveDate)
          : undefined,
        // schedule: hasValidSchedule
        //   ? convertTimeAndDaysToCronExpression({
        //       time,
        //       days,
        //     })
        //   : null,
        // startScheduleTime:
        //   !runReportDuringActiveTime && startScheduleDate
        //     ? convertMilestoneDateToISODate(startScheduleDate)
        //     : null,
        // endScheduleTime:
        //   !runReportDuringActiveTime && endScheduleDate
        //     ? convertMilestoneDateToISODate(endScheduleDate)
        //     : null,
      };
      const query = updateMilestoneMutation(
        selectedMilestone,
        updatedMilestone
      );
      await apolloClient.mutate({
        mutation: gql(query),
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });

      await onSuccess(selectedMilestone.externalId);
      notification.success({
        message: `${term.Milestone} updated`,
        description: `Successfully updated the ${term.milestone} "${updatedMilestone.name}"`,
      });
      onResetDrawer();
      onCancel();
    } catch (err: any) {
      notification.error({
        message: `Could not update ${term.milestone}`,
        description: err.toString(),
      });
    } finally {
      setCreating(false);
    }
  };

  const onCancelHandler = () => {
    if (creating) return;
    if (selectedMilestone && resetDrawer) {
      onResetDrawer();
    }
    setCurrentStep(0);
    onCancel();
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const renderNameStep = () => (
    <>
      <Input
        fullWidth
        placeholder={`Select a ${
          milestoneType === MilestoneType.Default
            ? term.milestone
            : term.collection
        } name`}
        value={nameInput}
        onChange={(e) => setNameInput(e.target.value)}
      />
      <StyledTextarea
        placeholder="Add description"
        value={descriptionInput}
        onChange={(e) => setDescriptionInput(e.target.value)}
      />

      <StyledButton type="primary" disabled={hasInvalidName} onClick={nextStep}>
        Next
      </StyledButton>
    </>
  );

  const renderActiveTimePeriodStep = () => (
    <>
      <Body level={2}>
        Add a time period when the {term.rule}{' '}
        {milestoneType === MilestoneType.Default
          ? term.milestone
          : term.collection}{' '}
        is active
      </Body>
      <DateRangeSelector
        placeholderStart={previousEndActiveTimeToString || undefined}
        allowEmpty={activeTimeAllowEmpty}
        onChange={(dateStrings) => {
          setStartActiveDate(dateStrings[0]?.length ? dateStrings[0] : null);
          setEndActiveDate(dateStrings[1]?.length ? dateStrings[1] : null);
        }}
        startDate={startActiveDate}
        endDate={endActiveDate}
        disabled={[disableStartActiveTime, false]}
      />
      {disableStartActiveTime && (
        <Body level={2}>
          The active period starts the day after the previous {term.milestone}{' '}
          ends.
        </Body>
      )}
      <StyledButton type="primary" onClick={nextStep}>
        Next
      </StyledButton>
    </>
  );

  // const renderRunReportScheduleStep = () => (
  //   <>
  //     <WIPLabel />
  //     <Body level={5}>
  //       Add a time when the Error Report will be run and updated
  //     </Body>

  //     <Group>
  //       <Body level={2}>Day</Body>
  //       <div style={{ position: 'relative' }}>
  //         <Select
  //           fullWidth
  //           disableTyping
  //           SelectAllLabel="Every day"
  //           enableSelectAll
  //           isMulti
  //           options={getDaysOptions()}
  //           placeholder="Add the day(s)"
  //           value={scheduleDay}
  //           onChange={setScheduleDay}
  //         />
  //       </div>
  //     </Group>

  //     <Group>
  //       <Body level={2}>Time</Body>
  //       <DateTimeSelector
  //         onChange={(val) => setScheduleTime(val)}
  //         value={scheduleTime}
  //         disabled={false}
  //       />
  //     </Group>

  //     <Group>
  //       <Body level={5}>
  //         {milestoneType === MilestoneType.Default
  //           ? 'The Error Report will be run on the selected days during the collection´s active time period.'
  //           : 'The Error Report will be run on the selected days during the collection’s active time period. If no time period is defined, the report will be run on the selected days during the project timeline.'}
  //       </Body>
  //     </Group>

  //     {!runReportDuringActiveTime && (
  //       <DateRangeSelector
  //         onChange={(dateStrings) => {
  //           setStartScheduleDate(
  //             dateStrings[0]?.length ? dateStrings[0] : null
  //           );
  //           setEndScheduleDate(dateStrings[1]?.length ? dateStrings[1] : null);
  //         }}
  //         startDate={startScheduleDate}
  //         endDate={endScheduleDate}
  //         disabled={[false, false]}
  //       />
  //     )}

  //     <StyledButton
  //       type="primary"
  //       disabled={hasInvalidRunReportSchedule}
  //       onClick={nextStep}
  //     >
  //       Done
  //     </StyledButton>
  //   </>
  // );

  const renderNameDescription = () =>
    nameInput || descriptionInput ? (
      <StyledStepDescription>
        {nameInput && <StyledBodyBold level={2}>{nameInput}</StyledBodyBold>}
        {descriptionInput && <Body level={2}>{descriptionInput}</Body>}
      </StyledStepDescription>
    ) : null;

  const renderActiveTimePeriodDescription = () =>
    startActiveDate || endActiveDate ? (
      <StyledStepRowDescription>
        {previousEndActiveTimeToString && (
          <Body level={2}>{previousEndActiveTimeToString}</Body>
        )}
        {startActiveDate && <Body level={2}>{startActiveDate}</Body>}
        <Body level={2}>to</Body>
        {endActiveDate && <Body level={2}>{endActiveDate}</Body>}
      </StyledStepRowDescription>
    ) : null;

  // const renderRunReportScheduleDescription = () =>
  //   scheduleDayLabels || scheduleTime || !runReportDuringActiveTime ? (
  //     <>
  //       <StyledStepDescription>
  //         {scheduleDayLabels.length > 0 && (
  //           <Body level={2}>
  //             {scheduleDayLabels.length === 1 ? 'Day' : 'Days'}:{' '}
  //             {scheduleDayLabels.length === 7
  //               ? 'Every day'
  //               : scheduleDayLabels.join(', ')}
  //           </Body>
  //         )}
  //         {scheduleTime && <Body level={2}>Time: {scheduleTime}</Body>}
  //       </StyledStepDescription>
  //       {!runReportDuringActiveTime ? (
  //         <Group>
  //           <Body level={2}>Run reports with custom schedule</Body>
  //           <StyledStepRowDescription>
  //             <Body level={2}>
  //               {startScheduleDate || 'Start date is missing.'}
  //             </Body>
  //             <Body level={2}>
  //               {endScheduleDate
  //                 ? `to ${endScheduleDate}`
  //                 : ' End date is missing.'}
  //             </Body>
  //           </StyledStepRowDescription>
  //         </Group>
  //       ) : (
  //         <StyledStepRowDescription>
  //           <Body level={2}>Run reports during active time</Body>
  //         </StyledStepRowDescription>
  //       )}
  //     </>
  //   ) : null;

  const stepSubtitle = (
    <Button type="ghost" icon="Edit" size="small" aria-label="Edit" />
  );
  const fallBackDescription = 'No details';
  const steps: Step[] = [
    {
      key: 'description',
      title: 'General information',
      subTitle: stepSubtitle,
      description: renderNameDescription() || fallBackDescription,
      content: renderNameStep(),
      invalid: hasInvalidName,
    },
    {
      key: 'active_time_period',
      title:
        milestoneType === MilestoneType.Static
          ? 'Active time period (optional)'
          : 'Active time period',
      subTitle: stepSubtitle,
      description: renderActiveTimePeriodDescription() || fallBackDescription,
      content: renderActiveTimePeriodStep(),
      invalid: hasInvalidActiveTimePeriod,
    },
    // {
    //   key: 'run_report_schedule',
    //   title: 'Run report schedule (optional)',
    //   subTitle: stepSubtitle,
    //   description: renderRunReportScheduleDescription() || fallBackDescription,
    //   content: renderRunReportScheduleStep(),
    //   invalid: hasInvalidRunReportSchedule,
    // },
  ];

  const renderFooter = () => (
    <StyledFooter>
      <Button onClick={onCancelHandler}>Cancel</Button>
      <Tooltip
        content={handleErrorMessages()}
        disabled={!creating && !hasInvalidValues}
      >
        <StyledButton
          type="primary"
          onClick={() =>
            selectedMilestone ? onUpdateMilestone() : onCreateMilestone()
          }
          loading={creating}
          disabled={creating || hasInvalidValues}
        >
          {selectedMilestone ? 'Update' : 'Create'}
        </StyledButton>
      </Tooltip>
    </StyledFooter>
  );

  return (
    <StyledDrawer
      width="90%"
      visible={visible}
      onCancel={onCancelHandler}
      footer={renderFooter()}
      title={
        selectedMilestone
          ? `Edit ${
              milestoneType === MilestoneType.Default
                ? term.milestone
                : term.openCollection
            }`
          : `Create ${
              milestoneType === MilestoneType.Default
                ? term.milestoneCollection
                : term.openCollection
            }`
      }
    >
      <StyledCol>
        <Body level={2}>
          {milestoneType === MilestoneType.Static &&
            `An ${term.openCollection} of ${term.rules} is a single ${term.collection} of ${term.rules}, not directly connection to the project timeline`}
          {milestoneType === MilestoneType.Default &&
            `A ${term.milestoneCollection} allows you to modify ${term.rules} and ${term.rulesets} on a timeline. The changes you make in one ${term.milestoneCollection} will apply to the following as default.`}
        </Body>
        <DrawerSteps
          steps={steps}
          currentStep={currentStep}
          onChangeStep={(step: number) => setCurrentStep(step)}
        />
      </StyledCol>
    </StyledDrawer>
  );
};

const StyledDrawer = styled(Drawer)`
  overflow: hidden;
  .cogs-drawer-header {
    border-bottom: 1px solid var(--cogs-border-default);
    justify-content: space-between;
    align-items: center;
    padding: 1rem 2rem 1rem 3rem;
    display: flex;
  }
  .cogs-drawer-title {
    font-weight: bold;
    font-size: 24px;
    line-height: 32px;
  }
  .drawer-content-wrapper {
    max-width: 886px;
    overflow: hidden;
  }
  .cogs-drawer-content {
    padding: 1rem 3rem;
  }
  .cogs-drawer-close {
    cursor: pointer;
    border-bottom: 0;
  }
`;

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledBodyBold = styled(Body)`
  font-weight: 500;
`;

const StyledStepDescription = styled.div`
  margin-top: 0.5rem;
`;

const StyledStepRowDescription = styled.div`
  margin-top: 0.5rem;
  flex-direction: row;
  display: flex;
  gap: 0.5rem;
`;

// const Group = styled.div``;

const StyledTextarea = styled(Textarea)`
  min-height: 100px;
  flex-direction: column;
  display: flex;
`;

const StyledButton = styled(Button)``;

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
`;
